import { InfoIcon, TriangleAlertIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

type NoticeProps = {
  tone?: 'info' | 'warning';
  children: ReactNode;
  /** One control that resolves the situation, for example "Unarchive". */
  action?: ReactNode;
  className?: string;
};

/**
 * A standing message about the state of what is on the page ("This workspace is archived"). Not for errors
 * (`error-state`, inline `role="alert"`) and not for the result of an action (`toast`).
 */
function Notice({ tone = 'info', children, action, className }: NoticeProps) {
  const Icon = tone === 'warning' ? TriangleAlertIcon : InfoIcon;
  return (
    // biome-ignore lint/a11y/useSemanticElements: `note` has no HTML element; <aside> would add a landmark per notice
    <div
      role="note"
      data-slot="notice"
      data-tone={tone}
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-sm px-4 py-3 text-sm',
        tone === 'warning'
          ? 'bg-status-warning-bg text-status-warning'
          : 'bg-status-info-bg text-status-info',
        className,
      )}
    >
      <Icon aria-hidden="true" className="size-5 shrink-0" />
      <p className="min-w-0 flex-1 basis-48">{children}</p>
      {action}
    </div>
  );
}

export { Notice };
